import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface UseContactFiltersReturn {
  uniqueTags: string[];
  uniqueCampaigns: string[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useContactFilters(): UseContactFiltersReturn {
  const { user } = useAuth();
  const [uniqueTags, setUniqueTags] = useState<string[]>([]);
  const [uniqueCampaigns, setUniqueCampaigns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUniqueValues = async () => {
    if (!user?.account_id || !supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: contacts, error: fetchError } = await supabase
        .from('account_contacts')
        .select('tags, campaigns')
        .eq('account_id', user.account_id);

      if (fetchError) {
        console.error('❌ [useContactFilters] Erro ao buscar contatos:', fetchError);
        setError(fetchError.message);
        return;
      }

      if (!contacts) {
        setUniqueTags([]);
        setUniqueCampaigns([]);
        return;
      }

      const tagsSet = new Set<string>();
      const campaignsSet = new Set<string>();

      contacts.forEach(contact => {
        if (Array.isArray(contact.tags)) {
          contact.tags.forEach((tag: string) => {
            if (tag && tag.trim()) {
              tagsSet.add(tag.trim());
            }
          });
        }

        if (Array.isArray(contact.campaigns)) {
          contact.campaigns.forEach((campaign: string) => {
            if (campaign && campaign.trim()) {
              campaignsSet.add(campaign.trim());
            }
          });
        }
      });

      const sortedTags = Array.from(tagsSet).sort((a, b) =>
        a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
      );

      const sortedCampaigns = Array.from(campaignsSet).sort((a, b) =>
        a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
      );

      setUniqueTags(sortedTags);
      setUniqueCampaigns(sortedCampaigns);

      console.log('✅ [useContactFilters] Valores únicos carregados:', {
        tags_count: sortedTags.length,
        campaigns_count: sortedCampaigns.length,
        tags: sortedTags,
        campaigns: sortedCampaigns
      });

    } catch (err) {
      console.error('💥 [useContactFilters] Erro inesperado:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUniqueValues();
  }, [user?.account_id]);

  return {
    uniqueTags,
    uniqueCampaigns,
    loading,
    error,
    refetch: fetchUniqueValues
  };
}
